import { Injectable, signal } from '@angular/core';

/**
 * The doors' exit handoff - lets a door finish its own dissolve BEFORE the
 * boot veil covers the screen.
 *
 * Signing in re-arms the veil the moment the uid lands (`app.ts`), and
 * since kit 2.2.0 the veil covers INSTANTLY - so without this, the door's
 * fade-out plays invisibly beneath an already-opaque cover and the member
 * sees a hard cut. The choreography the doors want is the opposite order:
 *
 *   1. credentials proven (`auth.signIn` resolved),
 *   2. the door fades its WHOLE content to the bare background,
 *   3. the veil arms - instant and opaque, but over the same bare
 *      background the door just faded to, so the only visible change is
 *      the loader appearing,
 *   4. navigation, under the veil.
 *
 * The door calls {@link hold} BEFORE it signs in (the uid must never race
 * the veil up) and {@link release} when its dissolve has finished - in a
 * `finally`, so a failed sign-in can never leave the veil held off for the
 * rest of the session. The shell's re-arm effect reads {@link holding} and
 * simply waits its turn.
 *
 * This changes WHEN the cover appears, never what it covers: the veil is
 * still opaque from its first frame, and navigation still happens beneath
 * it (the previous requirement - no content visible behind the veil -
 * stands).
 */
@Injectable({ providedIn: 'root' })
export class DoorExit {
  private readonly _holding = signal(false);
  private quiet = false;

  /** A door is mid-dissolve: the shell must not arm the veil yet. */
  readonly holding = this._holding.asReadonly();

  /** Claim the exit - call BEFORE `auth.signIn`, so the uid landing cannot
   *  race the veil up over an undissolved door. */
  hold(): void {
    this._holding.set(true);
  }

  /** The dissolve is done (or the sign-in failed) - the shell may cover.
   *  Idempotent; always call from a `finally`. */
  release(): void {
    this.quiet = false;
    this._holding.set(false);
  }

  /**
   * Release WITHOUT the veil: the door stayed on screen through the whole
   * wait (its own busy state carried the story, and data initialisation
   * already finished) and hands over with an ordinary page transition.
   * The shell consumes the quietness once - the NEXT sign-in arms as
   * normal.
   */
  releaseQuietly(): void {
    this.quiet = true;
    this._holding.set(false);
  }

  /** Shell-side: was this release quiet? Reading consumes the answer. */
  consumeQuietRelease(): boolean {
    const wasQuiet = this.quiet;
    this.quiet = false;
    return wasQuiet;
  }
}
